/**
 * KYC NOTIFICATION SERVICE
 * 
 * Service for sending KYC status change notifications via email
 * using the existing Resend email infrastructure.
 */

import { KYCStatusEmailData } from '../email/templates/KYCStatusEmailTemplate';
import { getSessionHeaders } from '../serverSession';
// SECURITY: All DB access goes through server-side API - NO getServiceRoleClient() in browser
async function callKycNotificationAPI(operation: string, params: Record<string, any> = {}) {
 const res = await fetch('/api/user/kyc-notification', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
 body: JSON.stringify({ operation, ...params })
 });
 if (!res.ok) throw new Error(`KYC Notification API error: ${res.status}`);
 return res.json();
}
import { resendEmailService } from '../resendEmailService';

export interface KYCNotificationData {
 kycId: string;
 userId: number;
 status: 'approved' | 'rejected';
 rejectionReason?: string;
 comments?: string;
 adminEmail: string;
}

export class KYCNotificationService {
 // SECURITY: No direct DB client - all operations go through API

 constructor() {
 console.log('KYC Notification Service initialized with existing email service');
 }

 /**
 * Send KYC status notification email to user
 */
 async sendKYCStatusNotification(notificationData: KYCNotificationData): Promise<{ success: boolean; error?: string }> {
 try {
 console.log(`Sending KYC ${notificationData.status} notification for user ${notificationData.userId}`);

 // Get user and KYC information
 const userInfo = await this.getUserAndKYCInfo(notificationData.userId, notificationData.kycId);
 if (!userInfo) {
 return { success: false, error: 'User or KYC information not found' };
 }

 // Prepare email data
 const emailData: KYCStatusEmailData = {
 fullName: userInfo.kyc.full_legal_name || `${userInfo.kyc.first_name} ${userInfo.kyc.last_name}`,
 email: userInfo.user.email,
 username: userInfo.user.username,
 status: notificationData.status,
 rejectionReason: notificationData.rejectionReason,
 comments: notificationData.comments,
 submissionDate: userInfo.kyc.created_at,
 reviewDate: new Date().toISOString(),
 nextSteps: this.getNextSteps(notificationData.status)
 };

 // Send email using existing ResendEmailService
 const emailResult = await this.sendKYCEmailDirectly({
 email: userInfo.user.email,
 fullName: emailData.fullName,
 status: notificationData.status,
 rejectionReason: notificationData.rejectionReason,
 comments: notificationData.comments,
 submissionDate: emailData.submissionDate,
 reviewDate: emailData.reviewDate,
 nextSteps: emailData.nextSteps || this.getNextSteps(notificationData.status)
 });

 if (!emailResult.success) {
 console.error('Failed to send KYC notification email:', emailResult.error);
 return { success: false, error: emailResult.error || 'Failed to send email' };
 }

 // Log the notification
 await this.logNotification(notificationData, emailResult.messageId || null);

 console.log(`KYC ${notificationData.status} notification sent successfully`);
 return { success: true };

 } catch (error) {
 console.error('Error sending KYC notification:', error);
 return { 
 success: false, 
 error: error instanceof Error ? error.message : 'Unknown error' 
 };
 }
 }

 /**
 * Send KYC notification email directly using centralized email API
 */
 private async sendKYCEmailDirectly(emailData: {
 email: string;
 fullName: string;
 status: 'approved' | 'rejected';
 rejectionReason?: string;
 comments?: string;
 submissionDate: string;
 reviewDate: string;
 nextSteps: string;
 }): Promise<{ success: boolean; error?: string; messageId?: string }> {
 try {
 console.log('[EmailService] Sending KYC notification via centralized API:', emailData.status);

 // FIXED: Use centralized backend email API instead of frontend Resend
 const emailType = emailData.status === 'approved' ? 'kyc_approval' : 'kyc_rejection';

 const response = await fetch('/api/send-email', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json', ...getSessionHeaders() },
 body: JSON.stringify({
 emailType,
 data: {
 email: emailData.email,
 fullName: emailData.fullName,
 rejectionReason: emailData.rejectionReason,
 comments: emailData.comments,
 submissionDate: emailData.submissionDate,
 reviewDate: emailData.reviewDate,
 dashboardUrl: `${typeof window !== 'undefined' ? window.location.origin : 'https://aureus.africa'}/dashboard`
 }
 })
 });

 if (!response.ok) {
 const error = await response.json();
 console.error('[EmailService] Failed to send KYC notification:', error);
 return { success: false, error: error.error || 'API request failed' };
 }

 const result = await response.json();
 console.log('[EmailService] KYC notification sent successfully:', result.messageId);
 return {
 success: true,
 messageId: result.messageId
 };

 } catch (error) {
 console.error('[EmailService] Error sending KYC email:', error);
 return {
 success: false,
 error: error instanceof Error ? error.message : 'Unknown error'
 };
 }
 }

 /**
 * Get user and KYC information for notification
 */
 private async getUserAndKYCInfo(userId: number, kycId: string): Promise<{ user: any; kyc: any } | null> {
 try {
 // Get user and KYC information via API
 const result = await callKycNotificationAPI('get_user_and_kyc_info', { userId, kycId });
 if (!result.success) {
 console.error('Error fetching user/KYC information:', result.error);
 return null;
 }
 const { user, kyc } = result.data;

 if (!user || !kyc) {
 console.error('Error fetching user/KYC information: user or kyc data missing');
 return null;
 }

 return { user, kyc };

 } catch (error) {
 console.error('Error in getUserAndKYCInfo:', error);
 return null;
 }
 }

 /**
 * Get next steps based on KYC status
 */
 private getNextSteps(status: 'approved' | 'rejected'): string {
 if (status === 'approved') {
 return 'Your account is now fully verified. You can access all platform features including share purchases, commission withdrawals, and certificate requests.';
 } else {
 return 'Please review the feedback provided, make necessary corrections, and resubmit your KYC verification through your dashboard.';
 }
 }

 /**
 * Log notification for audit trail
 */
 private async logNotification(notificationData: KYCNotificationData, messageId: string | null) {
 try {
 // Create notification log entry
 const logResult = await callKycNotificationAPI('log_notification', { logData: {
 user_id: notificationData.userId,
 notification_type: 'kyc_status_change',
 status: 'sent',
 email_message_id: messageId,
 metadata: {
 kyc_id: notificationData.kycId,
 kyc_status: notificationData.status,
 admin_email: notificationData.adminEmail,
 rejection_reason: notificationData.rejectionReason,
 comments: notificationData.comments
 },
 created_at: new Date().toISOString()
 }});

 if (!logResult.success) {
 console.warn('Failed to log KYC notification:', logResult.error);
 } else {
 console.log('KYC notification logged successfully');
 }

 } catch (error) {
 console.warn('Error logging KYC notification:', error);
 }
 }

 /**
 * Send bulk KYC notifications (for batch processing)
 */
 async sendBulkKYCNotifications(notifications: KYCNotificationData[]): Promise<{
 successful: number;
 failed: number;
 errors: Array<{ userId: number; error: string }>;
 }> {
 console.log(`Sending ${notifications.length} bulk KYC notifications...`);

 let successful = 0;
 let failed = 0;
 const errors: Array<{ userId: number; error: string }> = [];

 // Process notifications in batches to avoid rate limiting
 const batchSize = 5;
 for (let i = 0; i < notifications.length; i += batchSize) {
 const batch = notifications.slice(i, i + batchSize);
 
 const promises = batch.map(async (notification) => {
 const result = await this.sendKYCStatusNotification(notification);
 if (result.success) {
 successful++;
 } else {
 failed++;
 errors.push({
 userId: notification.userId,
 error: result.error || 'Unknown error'
 });
 }
 });

 await Promise.all(promises);

 // Add delay between batches to respect rate limits
 if (i + batchSize < notifications.length) {
 await new Promise(resolve => setTimeout(resolve, 1000));
 }
 }

 console.log(`Bulk KYC notifications completed: ${successful} successful, ${failed} failed`);
 return { successful, failed, errors };
 }

 /**
 * Test email configuration
 */
 async testEmailConfiguration(): Promise<{ success: boolean; error?: string }> {
 try {
 // Test using existing email service
 const testResult = await resendEmailService.sendEmail({
 to: 'support@aureus.africa',
 subject: 'KYC Notification Service Test',
 html: '<p>This is a test email from the KYC Notification Service.</p>',
 text: 'This is a test email from the KYC Notification Service.'
 });

 return testResult;

 } catch (error) {
 return {
 success: false,
 error: error instanceof Error ? error.message : 'Unknown error'
 };
 }
 }
}

// Export singleton instance
export const kycNotificationService = new KYCNotificationService();
